import {
  EnvironmentCommandRejectedError,
  type EnvironmentCommandRejectedReceipt,
} from "@afar-research/contracts";
import * as Schema from "effect/Schema";

import { sanitizeThreadErrorMessage } from "./transport.ts";

const isEnvironmentCommandRejectedError = Schema.is(EnvironmentCommandRejectedError);

/** Why the Environment Service refused a command: its code, reason, and observed revision. */
export type EnvironmentCommandRejection = EnvironmentCommandRejectedReceipt["result"];

/**
 * Read the reason out of a failed command, or `null` when the failure was not a
 * rejection.
 *
 * `EnvironmentCommandRejectedError` declares no `message` field, so it inherits
 * an empty `Error.message` and the reason is reachable only through its
 * receipt. Anything that turns a command failure into words has to come through
 * here, or it reports nothing at all.
 */
export function environmentCommandRejection(error: unknown): EnvironmentCommandRejection | null {
  return isEnvironmentCommandRejectedError(error) ? error.receipt.result : null;
}

/**
 * Describe a failed command in one non-empty sentence, for a surface that shows
 * every failure. `fallback` is used only when the failure carries no words of
 * its own.
 */
export function commandFailureMessage(error: unknown, fallback: string): string {
  const rejection = environmentCommandRejection(error);
  // The receipt types its reason as non-empty, but the runtime check that
  // recognizes a rejection does not enforce that refinement, and a blank reason
  // would reproduce the very silence this module exists to prevent. The
  // guarantee is made here rather than assumed of the sender.
  const message =
    rejection !== null
      ? rejection.message.trim()
      : error instanceof Error
        ? error.message.trim()
        : "";
  return message.length > 0 ? message : fallback;
}

/**
 * Describe a failed command for a thread error banner, or `null` when the
 * banner should stay closed.
 *
 * A dropped connection is noise the thread surfaces deliberately hide. A
 * rejection is a decision the Environment Service made and is always worth
 * showing, so it bypasses that filter rather than risking erasure by a reason
 * that happens to read like a transport error.
 */
export function threadCommandFailureMessage(error: unknown, fallback: string): string | null {
  const message = commandFailureMessage(error, fallback);
  return environmentCommandRejection(error) !== null
    ? message
    : sanitizeThreadErrorMessage(message);
}
